import Service, { service } from '@ember/service'
import { dropTask, task, timeout } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import {
  buildSchoolStudentsCsvApiValidateUrl,
  buildTeacherStudentsCsvApiValidateUrl,
  buildSchoolStudentsCsvApiImportUrl,
  buildTeacherStudentsCsvApiImportUrl,
  buildSchoolStudentsCsvApiValidateResultUrl,
  buildTeacherStudentsCsvApiValidateResultUrl,
} from 'teacher-ui-client/utils/import-students-csv-urls'
import { isArray } from '@ember/array'
import type { CandidateStudents, StudentImportError } from 'teacher-ui-client/student-import/utils'
import { mapJsonApiErrorToRowError, normalizeValidateCsvResponse } from 'teacher-ui-client/student-import/utils'
import PollingStatus from 'teacher-ui-client/student-import/polling-status'
import { format } from 'date-fns'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type SessionService from 'teacher-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import type Store from '@ember-data/store'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type ImportSchedule from 'teacher-ui-client/models/import-schedule'
import type ImportValidation from 'teacher-ui-client/models/import-validation'
import { err, ok } from 'neverthrow'

export enum JobType {
  validate = 'validate',
  import = 'import',
}

export enum ErrorType {
  validation = 'validation',
  pollingFailed = 'pollingFailed',
  resourceConflict = 'resourceConflict',
  missingParam = 'missingParam',
  unknown = 'unknown',
}

interface ValidationError {
  errorType: ErrorType.validation
  errors: StudentImportError[] | { detail: string }[]
}

export interface PollingFailedError {
  errorType: ErrorType.pollingFailed
  pollingStatus: PollingStatus
  response: Response
}

interface ResourceConflictError {
  errorType: ErrorType.resourceConflict
  response: Response
}

interface UnknownError {
  errorType: ErrorType.unknown
  response: Response
}

interface MissingParamError {
  errorType: ErrorType.missingParam
  key: 'file' | 'timestamp'
}

export function isPollingFailedError(error: { errorType: string }): error is PollingFailedError {
  return error.errorType === ErrorType.pollingFailed.toString()
}

export interface ImportJob {
  file?: File // the file for this import job
  timestamp?: string // a timestamp that the BE uses for when this file was validated
  subscriptionType?: SubscriptionType
  valid?: boolean // true (file has been validated), false (file has been invalidated), undefined (file not yet validated)
  candidateStudents?: CandidateStudents // for a valid file, this is the list of candidate students to display on the confirm page
  validationErrors?: StudentImportError[] // for an invalid file, this is the list of errors to display on the invalid page
  imported?: boolean // true (file successfully imported), false (file failed to be imported), undefined (file not yet imported)
}

export interface ValidateCsvResponse {
  data: {
    attributes: {
      status: string
    }
    id: string
    type: 'import-validations'
  }
  errors?: StudentImportError[]
}

export interface ImportCsvResponse {
  data: {
    id: string
  }
  errors?: StudentImportError[]
}

export interface ValidateCsvSuccessResponse {
  data: {
    id: string
    attributes: {
      'csv-row': string
      'first-name': string
      'last-name': string
      'grade-position': string
      'student-id'?: string
      'external-id': string
      'duplicate-source'?: boolean
      'duplicate-destination'?: boolean
      'student-name-change'?: boolean
      'teacher-email': string
      'school-class-name': string
    }
  }[]
}

export default class StudentImportService extends Service {
  @service session!: SessionService

  @service intl!: IntlService

  @service store!: Store

  @service authToken!: AuthToken

  @service flashQueue!: FlashQueueService

  @service resourceConflict!: ResourceConflictService

  timeoutMilliseconds = 1000

  importJob: ImportJob = {}

  // Call this to set a file for a new import job before transitioning to the validate page
  async setFile(selectedFile: File) {
    // Take a copy of the file so that it can't be edited on disk during this import job.
    // This isn't super critical, gravity has some checks to ensure a file hasn't changed between its validation & import
    const file = await this.cloneFile(selectedFile)

    const timestamp = this.getImportTimestamp()

    // New import job, from scratch, all flags reset
    this.importJob = { file, timestamp }
  }

  // Clone a file by converting its blob to an array buffer, and constructing a new blob and file from that
  cloneFile(selectedFile: File): Promise<File> {
    const fileReader = new FileReader()

    // FileReader API doesn't support async/await, so wrap it in a promise
    return new Promise((resolve, reject) => {
      fileReader.onerror = () => {
        fileReader.abort()
        reject(new Error('Problem reading input file for csv validation.'))
      }

      fileReader.onload = () => {
        const arrayBuffer = fileReader.result
        const clonedBlob = new Blob(arrayBuffer ? [arrayBuffer] : [], { type: selectedFile.type })
        const clonedFile = new File([clonedBlob], selectedFile.name, { type: selectedFile.type })
        resolve(clonedFile)
      }
      fileReader.readAsArrayBuffer(selectedFile)
    })
  }

  getImportTimestamp() {
    return format(new Date(), this.intl.t('dateFormats.dateFns.d-MMM-yy HH:mm'))
  }

  private get currentTeacher() {
    return this.session.currentTeacher
  }

  private get currentSchool() {
    return this.session.currentSchool
  }

  /**
   * Upload a csv file to the validate csv url, receive a job id and status in response. This job id can then be used
   * to poll the same endpoint for job completion. When complete, use the job id to query the validation result
   */
  validateCsv = dropTask(
    waitFor(async (subscriptionType: SubscriptionType) => {
      const { file, timestamp } = this.importJob

      if (file === undefined) {
        const error: MissingParamError = { errorType: ErrorType.missingParam, key: 'file' }
        return err(error)
      }
      if (timestamp === undefined) {
        const error: MissingParamError = { errorType: ErrorType.missingParam, key: 'timestamp' }
        return err(error)
      }

      const validateUrl = this.currentTeacher.subcoFor(subscriptionType)
        ? buildSchoolStudentsCsvApiValidateUrl({
            schoolId: this.currentSchool.id,
            subscriptionType,
          })
        : buildTeacherStudentsCsvApiValidateUrl({
            teacherId: this.currentTeacher.id,
            subscriptionType,
          })
      const response = await this.uploadFile(file, validateUrl, subscriptionType, timestamp)
      const responseBody = (await response.json()) as ValidateCsvResponse

      const jobId = responseBody.data?.id ?? null

      // Handle a failed upload
      if (!response.ok || !jobId) {
        if (isConflictError(response)) {
          const error: ResourceConflictError = { errorType: ErrorType.resourceConflict, response }
          return err(error)
        }
        /* There is a special case where when the uploaded file is not UTF-8 encoded, the backend can immediately return
         * a 422 response. Ideally, the backend would always return a 200 OK response here with a jobID, and do the
         * encoding check as part of the validation job, returning this type of error AFTER polling complete, in the final
         * GET validate result step below, where all other 422 errors are handled.
         *
         * Once the encoding check in Gravity is moved from the request pipeline to the job, then this 422 check here can
         * be removed.
         * https://blakeelearning.atlassian.net/browse/YETI-410
         */
        if (response.status === 422) {
          // Should be an UTF-8 encoding issue with the file
          this.importJob.valid = false
          this.importJob.validationErrors = this.formatValidationErrors(responseBody)
          const error: ValidationError = {
            errorType: ErrorType.validation,
            errors: this.importJob.validationErrors,
          }
          return err(error)
        } else {
          const error: UnknownError = { errorType: ErrorType.unknown, response }
          // Something else went wrong, caller can decide what to do with the response
          return err(error)
        }
      }

      const pollingStatus = await this.pollServerForUploadStatus.perform(jobId, JobType.validate)

      if (pollingStatus.hasFailed()) {
        const error: PollingFailedError = {
          errorType: ErrorType.pollingFailed,
          pollingStatus,
          response,
        }
        return err(error)
      }

      if (pollingStatus.isComplete()) {
        // Successfully processed CSV to be either valid or invalid
        // Retreive the validation result from the result endpoint using the job id

        const headers = this.requestHeaders()

        const validateResultUrlWithoutJobId = this.currentTeacher.subcoFor(subscriptionType)
          ? buildSchoolStudentsCsvApiValidateResultUrl({
              schoolId: this.currentSchool.id,
              subscriptionType,
            })
          : buildTeacherStudentsCsvApiValidateResultUrl({
              teacherId: this.currentTeacher.id,
              subscriptionType,
            })
        const validateResultUrl = `${validateResultUrlWithoutJobId}/${jobId}`

        const resultResponse = await fetch(validateResultUrl, {
          method: 'GET',
          headers,
        })

        if (resultResponse?.ok) {
          const resultResponseBody = (await resultResponse.json()) as ValidateCsvSuccessResponse
          this.importJob.valid = true
          this.importJob.subscriptionType = subscriptionType // So the service knows what type the file was validated for, when we go to import it
          this.importJob.candidateStudents = normalizeValidateCsvResponse(resultResponseBody)
          return ok(resultResponseBody)
        }

        const resultResponseBody = (await resultResponse.json()) as ValidateCsvResponse
        if (resultResponse?.status === 422) {
          this.importJob.valid = false
          this.importJob.validationErrors = this.formatValidationErrors(resultResponseBody)
          const error: ValidationError = {
            errorType: ErrorType.validation,
            errors: this.importJob.validationErrors,
          }
          return err(error)
        } else if (isConflictError(resultResponse)) {
          const error: ResourceConflictError = { errorType: ErrorType.resourceConflict, response }
          return err(error)
        }
      }
      const error: UnknownError = { errorType: ErrorType.unknown, response }
      return err(error)
    }),
  )

  /**
   * Use this to upload a csv file to a url with the necessary headers
   */
  async uploadFile(file: File, url: string, subscriptionType: SubscriptionType, timestamp: string) {
    const headers = this.requestHeaders()

    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('Content-Type', file.type)
    formData.append('timestamp', timestamp)
    formData.append('subscriptionTypeLocal', this.intl.t(subscriptionType))
    formData.append('async', 'true')

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    return response
  }

  formatValidationErrors(responseBody: ValidateCsvResponse) {
    const errors = responseBody?.errors
    if (isArray(errors)) {
      return errors.map((error) => {
        return mapJsonApiErrorToRowError(error, responseBody, this.intl)
      })
    }
    return []
  }

  /**
   * A task to upload a csv to the server and then starts polling an endpoint to
   * retrieve the status of processing. On error we will show a flash message. On success we will redirect to
   * the manage.students page which reloads the student data. Because the task is running on the service we can
   * invoke the redirect from any location.
   *
   * @type {Task}
   * @returns {PollingStatus}
   */
  importCsv = dropTask(
    waitFor(async () => {
      // get the needed arguments from the import job & ensuring it was validated
      const { file, timestamp, subscriptionType, valid } = this.importJob
      if (!valid || !file || !subscriptionType || !timestamp) return { response: { ok: false }, responseBody: {} }

      const importUrl = this.currentTeacher.subcoFor(subscriptionType)
        ? buildSchoolStudentsCsvApiImportUrl({
            schoolId: this.currentSchool.id,
            subscriptionType,
          })
        : buildTeacherStudentsCsvApiImportUrl({
            teacherId: this.currentTeacher.id,
            subscriptionType,
          })

      const response = await this.uploadFile(file, importUrl, subscriptionType, timestamp)
      const responseBody = (await response.json()) as ImportCsvResponse
      const jobId = responseBody.data?.id ?? null
      if (!response.ok || !jobId) {
        if (isConflictError(response)) {
          this.resourceConflict.handleError('importCsv', response)
          return { response: { ok: false }, responseBody: {} }
        }
        return { response, responseBody }
      }
      this.flashQueue.addInfo({ subtitle: this.intl.t('manage.studentCsv.importStudents', { htmlSafe: true }) })
      const pollingStatus = await this.pollServerForUploadStatus.perform(jobId, JobType.import)
      if (pollingStatus.hasFailed()) {
        this.importJob.imported = false
        return { response: { ok: false }, responseBody: { pollingStatus } }
      }
      if (pollingStatus.isComplete()) {
        this.importJob.imported = true
        return { response: { ok: true }, responseBody: { pollingStatus } }
      }
      return { response: { ok: false }, responseBody: {} }
    }),
  )

  /**
   * Creates a task which polls gravity infinitely to ask for a result response and will
   * continue until the status is either completed or error. We will pause for x seconds after
   * the request finished and start again until we are done.
   *
   * @param {String} jobId
   * @param {String} jobType - either 'validate' or 'import', indicates what endpoint to poll
   * @returns {PollingStatus} // returns a polling status object with all information about the polling request
   */
  pollServerForUploadStatus = dropTask(async (jobId: string, jobType: JobType) => {
    await timeout(this.timeoutMilliseconds) // wait x milliseconds before we start the polling
    // fetch response
    // get json data of the response
    // convert into PollingStatus object
    // [TJ] note: I keep this separate (even though it is duplicated), due to it's async behaviour within the task
    let jobModel: ImportValidation | ImportSchedule | null = await this.requestProgress.perform(jobId, jobType)
    let modelStatus: string | null = jobModel?.status || null
    let status: PollingStatus = this.getPollingStatusFromResponse(modelStatus, jobId)

    // continue polling as long as the status is not `complete`, `error` or null
    while (status.isStillRunning()) {
      await timeout(this.timeoutMilliseconds * 4) // wait x * 4 milliseconds after the last request finished
      jobModel = await this.requestProgress.perform(jobId, jobType)
      modelStatus = jobModel?.status || null
      status = this.getPollingStatusFromResponse(modelStatus, jobId)
    }

    return status
  })

  /**
   * Lets us cancel the running task which also cancels any child task
   */
  cancelValidateCsv() {
    void this.validateCsv.cancelAll()
  }

  cancelImportCsv() {
    void this.importCsv.cancelAll()
  }

  /**
   * Calls endpoint to retrieve an status object
   * @param {String} jobId
   * @param {JobType} jobType - either 'validate' or 'import'
   * @returns {Promise<ImportValidation | ImportSchedule>}
   */
  private requestProgress = task(async (jobId: string, jobType: JobType) => {
    const response =
      jobType === JobType.validate
        ? await this.store.findRecord<ImportValidation>('import-validation', jobId, { reload: true })
        : await this.store.findRecord<ImportSchedule>('import-schedule', jobId, { reload: true })
    return response
  })

  /**
   * If we cant get a status value this will always return null
   * and indicate that a response failed
   *
   * @param jobId
   * @returns {PollingStatus}
   * @private
   */
  private getPollingStatusFromResponse(status: string | null, jobId: string) {
    return new PollingStatus(status, jobId)
  }

  /**
   * Returns Headers which can be used for the student imports requests
   * @private
   */
  private requestHeaders() {
    const headers: Record<string, string> = {
      Accept: 'application/vnd.api+json,json-api',
    }
    if (this.authToken.token) {
      headers.Authorization = this.authToken.token
    }
    return headers
  }
}

declare module '@ember/service' {
  interface Registry {
    'student-import': StudentImportService
  }
}
